// DEPENDENT ON ORG TYPE. Used in method translateText
import { texts } from './texts/optional-translation.texts';

// NOT DEPENDENT ON ORG TYPE. Used in methods translate, localeTranslate
import { textMappings } from './texts/text-mappings';
// SAME AS ABOVE
import { CompanyAcronymsEnum, companyAcronymsMap, CompanyTerminologyEnum } from '@orgbrain/roles';
import { textMappings as boardmatchTextMappings } from './texts/boardmatch-text-mappings';
import { textMappingsFilebrowser } from './texts/filebrowser-text-mapping';

const combinedTestMappings = {
  ...textMappings,
  ...textMappingsFilebrowser,
  ...boardmatchTextMappings,
};

type TranslationMap = {
  en: string;
  nb: string;
};

type TranslationTypeMap = {
  default: TranslationMap;
  fliAlike: TranslationMap;
};

export function getPackTypeHelper(orgType: CompanyAcronymsEnum): CompanyTerminologyEnum {
  if (!companyAcronymsMap[orgType]) {
    return CompanyTerminologyEnum.default;
  } else {
    return companyAcronymsMap[orgType].terminolgy;
  }
}

export class OptionalTranslationHelper {
  constructor(
    public orgType: CompanyAcronymsEnum,
    public locale: string,
    public org?: any
  ) {}

  public getPackType(orgType = this.orgType): CompanyTerminologyEnum {
    const OT = orgType || this.orgType;
    return getPackTypeHelper(OT);
  }

  private performTranslation(
    translations: TranslationTypeMap,
    locale = this.locale,
    orgType = this.orgType
  ) {
    const packType = this.getPackType(orgType);
    let packType2 = packType;

    // Explanation of the below line.
    // Almost always, 'SUBGR-DD' will have same text as SUBGR
    // But as sometimes they are different (so far 1 time as of 2025-10-30)
    // So, we use SUBGR as fallback and 'SUBGR-DD' does not get it's own redundant translation
    if (orgType === 'SUBGR-DD') {
      packType2 = CompanyTerminologyEnum.SUBGR;
    }
    const finalAttempt = this.isSpecialCaseCompany()
      ? translations['fliAlike'] || translations['default']
      : translations['default'];
    const myPack =
      translations[orgType] ||
      translations[packType] ||
      translations[packType2] ||
      translations['alternative'] ||
      finalAttempt;
    return myPack[locale] || myPack['en'];
  }

  // It's for Org Type Translation (FLI/BRL/Other)
  // If translation is not found, now takes translation from textMappings as fallback
  public translateText(keyword, locale = null, orgType = null, mytexts = null) {
    let pack;
    locale = locale || this.locale;
    orgType = orgType || this.orgType;
    if (mytexts) {
      pack = mytexts[keyword];
    }

    if (!pack) {
      pack = texts[keyword];
    }

    if (pack) {
      return this.performTranslation(pack, locale, orgType);
    } else if (
      textMappings[keyword] &&
      (textMappings[keyword][locale] || textMappings[keyword]['en'])
    ) {
      return textMappings[keyword][locale] || textMappings[keyword]['en'];
    }
    {
      return keyword;
    }
  }

  // Came from OptionalTranslationService
  // Usage: this.optionalTranslationService.translate('isSubsidiaryText', options)
  // Every option is optional. Can be null, like translate('isSubsidiaryText')
  // Example: if you need variables and jurisdiction in your  translation, usage is like

  /*
   *  const options = {
   *    variables: {
   *      orgName: org.name,
   *    },
   *    jurisdiction: OrgJurisdiction.NO
   *  };
   */

  // Then in translations that variable should wrapped like this: {{orgName}}

  // In most cases, does not care about org type. But for jurisdiction translations may also use org type.
  // In general, this method is more flexible
  translate(key, { nameType, locale, jurisdiction, companyType, variables } = {} as any) {
    const proceedWithTranslation =
      typeof nameType !== 'string' ||
      nameType === 'KEY' ||
      nameType === 'KEY_MUTABLE' ||
      nameType === 'USER_ID' ||
      nameType === 'PERMISSION';

    if (!proceedWithTranslation) {
      return key;
    }

    const finalLocale = locale || this.locale;
    let finalKey = jurisdiction ? `${key}?jurisdiction=${jurisdiction}` : key;
    finalKey = companyType ? `${finalKey}?companyType=${companyType}` : finalKey;

    let translation = combinedTestMappings[finalKey] && combinedTestMappings[finalKey][finalLocale];
    if (typeof translation !== 'string') {
      translation = combinedTestMappings[finalKey] && combinedTestMappings[finalKey]['en'];
    }

    let translationWithOptionalReplacements;

    if (typeof translation === 'string') {
      translationWithOptionalReplacements = translation;
    } else {
      translationWithOptionalReplacements = this.translateText(finalKey, finalLocale, companyType);
    }

    if (variables) {
      const keys = Object.keys(variables);
      for (const key of keys) {
        const find = `{{${key}}}`;
        const re = new RegExp(find, 'g');
        translationWithOptionalReplacements = translationWithOptionalReplacements.replace(
          re,
          variables[key]
        );
      }
    }
    return translationWithOptionalReplacements;
  }

  // Came from OptionalTranslationService
  localeTranslate(key: string, locale: string) {
    return this.translate(key, { locale });
  }

  isFliAlike(): boolean {
    return this.getPackType() === CompanyTerminologyEnum.fliAlike;
  }

  isSpecialCaseCompany() {
    return this.isFliAlike() || ['BRL', 'ESEK', 'SAM'].includes(this.orgType);
  }

  isAsaAlike() {
    return this.orgType === 'ASA';
  }
  isAsLike() {
    return this.orgType === 'AS' || this.orgType === 'ASA';
  }
}

export const localeLessTranslator = new OptionalTranslationHelper(null as CompanyAcronymsEnum, '');
